import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../../services/api';
import './DocumentPromptModal.css';

/**
 * DocumentPromptModal - Modal for viewing, editing, saving, and deleting
 * a document's AI-generated prompts.
 *
 * Supports two modes:
 * - Composite: single graph_extraction prompt
 * - Compiler: two prompts (compiler_phase1 + compiler_phase3)
 *
 * The mode is auto-detected from which keys have document-level overrides.
 */
const DocumentPromptModal = ({
  isOpen,
  onClose,
  documentId,
  documentName,
  onPromptSaved,
  onPromptDeleted,
}) => {
  // 'composite' | 'compiler'
  const [mode, setMode] = useState('composite');
  // Composite state
  const [promptText, setPromptText] = useState('');
  const [originalText, setOriginalText] = useState('');
  // Compiler state
  const [phase1Text, setPhase1Text] = useState('');
  const [originalPhase1, setOriginalPhase1] = useState('');
  const [phase3Text, setPhase3Text] = useState('');
  const [originalPhase3, setOriginalPhase3] = useState('');
  const [activeTab, setActiveTab] = useState('phase1');

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const loadPrompt = useCallback(async () => {
    if (!documentId) return;
    setLoading(true);
    setError(null);
    setSuccess(null);
    setConfirmDelete(false);
    try {
      const response = await apiService.getDocumentPrompts(documentId);
      if (response.success && response.prompts) {
        const docOverrides = response.document_customizations || [];
        const hasCompilerOverrides =
          docOverrides.includes('compiler_phase1') || docOverrides.includes('compiler_phase3');
        const hasCompositeOverride = docOverrides.includes('graph_extraction');

        if (hasCompilerOverrides && !hasCompositeOverride) {
          setMode('compiler');
          const p1 = response.prompts.compiler_phase1;
          const p3 = response.prompts.compiler_phase3;
          const p1Content = p1?.content || '';
          const p3Content = p3?.content || '';
          setPhase1Text(p1Content);
          setOriginalPhase1(p1Content);
          setPhase3Text(p3Content);
          setOriginalPhase3(p3Content);
          setPromptText('');
          setOriginalText('');
        } else {
          setMode('composite');
          const graphExtraction = response.prompts.graph_extraction;
          const content = graphExtraction?.content || '';
          setPromptText(content);
          setOriginalText(content);
          setPhase1Text('');
          setOriginalPhase1('');
          setPhase3Text('');
          setOriginalPhase3('');
        }
      } else {
        setMode('composite');
        setPromptText('');
        setOriginalText('');
        setPhase1Text('');
        setOriginalPhase1('');
        setPhase3Text('');
        setOriginalPhase3('');
      }
    } catch (err) {
      console.error('Failed to load document prompt:', err);
      setError('Failed to load prompt. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [documentId]);

  useEffect(() => {
    if (isOpen && documentId) {
      loadPrompt();
    }
    if (!isOpen) {
      setPromptText('');
      setOriginalText('');
      setPhase1Text('');
      setOriginalPhase1('');
      setPhase3Text('');
      setOriginalPhase3('');
      setError(null);
      setSuccess(null);
      setConfirmDelete(false);
      setActiveTab('phase1');
    }
  }, [isOpen, documentId, loadPrompt]);

  const hasChanges = mode === 'compiler'
    ? (phase1Text !== originalPhase1 || phase3Text !== originalPhase3)
    : (promptText !== originalText);

  const handleSave = async () => {
    if (!documentId) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      let prompts;
      if (mode === 'compiler') {
        if (!phase1Text.trim() && !phase3Text.trim()) return;
        prompts = {};
        if (phase1Text.trim()) prompts.compiler_phase1 = phase1Text.trim();
        if (phase3Text.trim()) prompts.compiler_phase3 = phase3Text.trim();
      } else {
        if (!promptText.trim()) return;
        prompts = { graph_extraction: promptText.trim() };
      }

      const response = await apiService.updateDocumentPrompts(documentId, prompts);
      if (response.success) {
        if (mode === 'compiler') {
          setOriginalPhase1(phase1Text.trim());
          setOriginalPhase3(phase3Text.trim());
        } else {
          setOriginalText(promptText.trim());
        }
        setSuccess('Prompt saved successfully.');
        if (onPromptSaved) onPromptSaved();
      } else {
        setError(response.error || 'Failed to save prompt.');
      }
    } catch (err) {
      console.error('Failed to save document prompt:', err);
      setError(err.response?.data?.error || 'Failed to save prompt.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!documentId) return;
    setDeleting(true);
    setError(null);
    setSuccess(null);
    try {
      const response = await apiService.resetDocumentPrompts(documentId);
      if (response.success) {
        setSuccess('Prompt deleted. You can regenerate it using "Create Graph Prompt".');
        setPromptText('');
        setOriginalText('');
        setPhase1Text('');
        setOriginalPhase1('');
        setPhase3Text('');
        setOriginalPhase3('');
        if (onPromptDeleted) onPromptDeleted();
        setTimeout(() => {
          onClose();
        }, 1200);
      } else {
        setError(response.error || 'Failed to delete prompt.');
      }
    } catch (err) {
      console.error('Failed to delete document prompt:', err);
      setError(err.response?.data?.error || 'Failed to delete prompt.');
    } finally {
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  const handleRevert = () => {
    if (mode === 'compiler') {
      setPhase1Text(originalPhase1);
      setPhase3Text(originalPhase3);
    } else {
      setPromptText(originalText);
    }
    setError(null);
    setSuccess(null);
  };

  const hasContent = mode === 'compiler'
    ? (originalPhase1 || originalPhase3)
    : originalText;

  const currentText = mode === 'compiler'
    ? (activeTab === 'phase1' ? phase1Text : phase3Text)
    : promptText;

  if (!isOpen) return null;

  return (
    <div className="doc-prompt-modal-overlay" onClick={onClose}>
      <div className="doc-prompt-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="doc-prompt-modal__header">
          <div className="doc-prompt-modal__title-group">
            <h3>
              {mode === 'compiler'
                ? 'Document Compiler Prompts'
                : 'Document Graph Extraction Prompt'}
            </h3>
            <span className="doc-prompt-modal__doc-name" title={documentName}>
              {documentName}
            </span>
          </div>
          <button className="doc-prompt-modal__close" onClick={onClose}>
            &times;
          </button>
        </div>

        {/* Messages */}
        {error && (
          <div className="doc-prompt-modal__message doc-prompt-modal__message--error">
            <span>{error}</span>
            <button onClick={() => setError(null)}>&times;</button>
          </div>
        )}
        {success && (
          <div className="doc-prompt-modal__message doc-prompt-modal__message--success">
            <span>{success}</span>
            <button onClick={() => setSuccess(null)}>&times;</button>
          </div>
        )}

        {/* Body */}
        <div className="doc-prompt-modal__body">
          {loading ? (
            <div className="doc-prompt-modal__loading">Loading prompt...</div>
          ) : (
            <>
              {mode === 'compiler' ? (
                <>
                  <div className="doc-prompt-modal__info">
                    These are the AI-generated compiler prompts for this document.
                    Phase 1 handles atom extraction and Phase 3 handles ontology enrichment.
                    Phase 2 (Platform wiring) is domain-neutral and not customized per document.
                  </div>
                  <div className="doc-prompt-modal__tabs">
                    <button
                      className={`doc-prompt-modal__tab ${activeTab === 'phase1' ? 'doc-prompt-modal__tab--active' : ''}`}
                      onClick={() => setActiveTab('phase1')}
                    >
                      Phase 1 - Atom Extraction
                      {phase1Text !== originalPhase1 && <span className="doc-prompt-modal__tab-dot" />}
                    </button>
                    <button
                      className={`doc-prompt-modal__tab ${activeTab === 'phase3' ? 'doc-prompt-modal__tab--active' : ''}`}
                      onClick={() => setActiveTab('phase3')}
                    >
                      Phase 3 - Ontology Enrichment
                      {phase3Text !== originalPhase3 && <span className="doc-prompt-modal__tab-dot" />}
                    </button>
                  </div>
                  {activeTab === 'phase1' ? (
                    <textarea
                      className="doc-prompt-modal__editor"
                      value={phase1Text}
                      onChange={(e) => setPhase1Text(e.target.value)}
                      placeholder="No custom Phase 1 prompt set."
                      spellCheck={false}
                    />
                  ) : (
                    <textarea
                      className="doc-prompt-modal__editor"
                      value={phase3Text}
                      onChange={(e) => setPhase3Text(e.target.value)}
                      placeholder="No custom Phase 3 prompt set."
                      spellCheck={false}
                    />
                  )}
                </>
              ) : (
                <>
                  <div className="doc-prompt-modal__info">
                    This is the AI-generated graph extraction prompt for this document.
                    It is used instead of the default prompt when processing this document for graph building.
                  </div>
                  <textarea
                    className="doc-prompt-modal__editor"
                    value={promptText}
                    onChange={(e) => setPromptText(e.target.value)}
                    placeholder="No custom prompt set. Use 'Create Graph Prompt' to generate one."
                    spellCheck={false}
                  />
                </>
              )}
              <div className="doc-prompt-modal__stats">
                {currentText ? (
                  <>
                    <span>{currentText.length.toLocaleString()} characters</span>
                    <span>{currentText.split(/\s+/).filter(Boolean).length.toLocaleString()} words</span>
                  </>
                ) : (
                  <span className="doc-prompt-modal__stats--empty">No custom prompt</span>
                )}
                {hasChanges && <span className="doc-prompt-modal__stats--changed">Unsaved changes</span>}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="doc-prompt-modal__footer">
          <div className="doc-prompt-modal__footer-left">
            {hasContent && !confirmDelete && (
              <button
                className="doc-prompt-modal__btn doc-prompt-modal__btn--danger"
                onClick={() => setConfirmDelete(true)}
                disabled={saving || deleting}
              >
                Delete Prompt
              </button>
            )}
            {confirmDelete && (
              <div className="doc-prompt-modal__confirm-delete">
                <span>Delete this prompt?</span>
                <button
                  className="doc-prompt-modal__btn doc-prompt-modal__btn--danger"
                  onClick={handleDelete}
                  disabled={deleting}
                >
                  {deleting ? 'Deleting...' : 'Yes, Delete'}
                </button>
                <button
                  className="doc-prompt-modal__btn doc-prompt-modal__btn--secondary"
                  onClick={() => setConfirmDelete(false)}
                  disabled={deleting}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
          <div className="doc-prompt-modal__footer-right">
            {hasChanges && (
              <button
                className="doc-prompt-modal__btn doc-prompt-modal__btn--secondary"
                onClick={handleRevert}
                disabled={saving}
              >
                Revert
              </button>
            )}
            <button
              className="doc-prompt-modal__btn doc-prompt-modal__btn--primary"
              onClick={handleSave}
              disabled={!hasChanges || saving}
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button
              className="doc-prompt-modal__btn doc-prompt-modal__btn--secondary"
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentPromptModal;
